/* @jsx h */
import { defineComponent, ref, h, computed } from "vue";
import { Bundle, Product } from "@lemmonhq/types";
import { cn } from "@lemmonhq/uikit";
import { pp } from "@app/utils/clientBusiness";
import { LemmonModal, NumPad, useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "../../providers/Auth";

export type BundleModalProps = {
  product: Product;
  bundles: Bundle[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    bundles: { bundleId: number; optionIds: number[] }[],
    customPrice?: number,
  ) => void;
};
export const BundleModal = defineComponent<BundleModalProps>({
  name: "BundleModal",
  props: ["product", "bundles", "isOpen", "onClose", "onConfirm"],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const selectedOptions = ref<Record<number, number[]>>({});
    const customPrice = ref("");
    const isEnteringCustomPrice = ref(false);

    const hasValidCustomPrice = computed(() => {
      const parsed = parseFloat(customPrice.value);
      return Number.isFinite(parsed) && parsed > 0;
    });

    const handleOptionToggle = (bundleId: number, optionId: number) => {
      const currentSelected = selectedOptions.value[bundleId] || [];
      const isSelected = currentSelected.includes(optionId);
      const bundle = props.bundles.find((b) => b.id === bundleId);

      if (!bundle) return;
      let newSelected: number[];
      if (isSelected) {
        newSelected = currentSelected.filter((id) => id !== optionId);
      } else {
        if (currentSelected.length >= bundle.limit) {
          newSelected = [...currentSelected.slice(1), optionId];
        } else {
          newSelected = [...currentSelected, optionId];
        }
      }
      selectedOptions.value = {
        ...selectedOptions.value,
        [bundleId]: newSelected,
      };
    };

    const isValid = computed(() =>
      props.bundles.every((bundle) => {
        const selected = selectedOptions.value[bundle.id]?.length || 0;
        return selected >= bundle.min_limit && selected <= bundle.limit;
      }),
    );

    const handleConfirm = () => {
      if (!isValid.value) return;

      const formattedSelections = props.bundles.map((bundle) => ({
        bundleId: bundle.id,
        optionIds: selectedOptions.value[bundle.id] || [],
      }));

      const customP = customPrice.value ? parseFloat(customPrice.value) : 0;

      props.onConfirm(
        formattedSelections,
        props.product.is_custom ? customP || 0 : undefined,
      );
      props.onClose();
    };

    return () =>
      !props.isOpen ? null : (
        <LemmonModal
          title={t("pos.bundle.select_supplements")}
          onClose={props.onClose}
          height="calc(100dvh - 0px)"
          renderButtons={() => (
            <div class="border-t  bg-base-100 p-4">
              <div class="flex flex-col justify-between gap-2 sm:flex-row">
                <button
                  onClick={() => props.onClose()}
                  class="btn btn-secondary min-h-12 flex-1"
                >
                  {t("general.words.cancel")}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={
                    !isValid.value ||
                    (!!props.product.is_custom && !hasValidCustomPrice.value)
                  }
                  class="btn btn-primary min-h-12 flex-1"
                >
                  {t("general.words.add")}
                </button>
              </div>
            </div>
          )}
        >
          <div class="flex-1 p-4">
            {props.product.is_custom ? (
              <div class="mb-6 rounded-2xl border  bg-base-200/40 p-4">
                <label
                  for="customPrice"
                  class="block text-sm font-medium  mb-2"
                >
                  {t("pos.bundle.enter_custom_price")}
                </label>
                <button
                  id="customPrice"
                  type="button"
                  onClick={() => {
                    isEnteringCustomPrice.value = !isEnteringCustomPrice.value;
                  }}
                  class="flex min-h-14 w-full items-center justify-between rounded-xl border  bg-base-100 px-4 text-left text-accent"
                >
                  <span class="text-sm ">
                    {t("pos.bundle.enter_custom_price")}
                  </span>
                  <span class="text-2xl font-semibold">
                    {customPrice.value || "0"}
                  </span>
                </button>
                {isEnteringCustomPrice.value ? (
                  <div class="mt-4">
                    <NumPad
                      value={customPrice.value}
                      onChange={(nv) => {
                        customPrice.value = nv;
                      }}
                      secureDisplay={false}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            <div class="space-y-4">
              {props.bundles.map((bundle) => (
                <div key={bundle.id} class="mb-6 px-0 rounded border ">
                  <div class="flex justify-between items-center mb-2 border-b  py-3 px-3 bg-base-100 rounded">
                    <h3 class="font-medium text-accent uppercase">
                      {bundle.name}
                    </h3>
                    <span class="text-sm text-accent">
                      {`${t("pos.bundle.select_count")} ${bundle.min_limit}/${bundle.limit}`}
                    </span>
                  </div>
                  <div class="space-y-2">
                    {bundle.options
                      .filter((el) => el.display.status === "available")
                      .map((option) => {
                        const isSelected =
                          selectedOptions.value[bundle.id]?.includes(
                            option.id,
                          ) || false;
                        return (
                          <div
                            key={option.id}
                            onClick={() =>
                              handleOptionToggle(bundle.id, option.id)
                            }
                            class={cn(
                              "flex justify-between items-center p-3 rounded cursor-pointer",
                              "",
                              isSelected && "bg-[#9bcaff] text-black",
                            )}
                          >
                            <div>
                              <p>{option.display.title}</p>
                              {option.display.description && (
                                <p class="text-sm ">
                                  {option.display.description}
                                </p>
                              )}
                            </div>
                            <span class="text-sm">
                              {option.price > 0
                                ? `+${option.price.toFixed(2)} ${auth.user.value?.restaurant.currency || "CHF"}`
                                : t("pos.bundle.included")}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </LemmonModal>
      );
  },
});
